// Single source of truth for /tools/closing-disclosure-review.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/closing-disclosure-review";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "How to Review Your Closing Disclosure for Errors or Junk Fees";
export const PAGE_DESCRIPTION =
  "A page-by-page Closing Disclosure review checklist: compare it to your Loan Estimate, spot junk fees on Page 2, and use a copy-paste email template to challenge charges before you sign.";

export const HERO_PROMISE =
  "You get your Closing Disclosure at least three business days before closing, and that window exists so you can actually check it. Set the CD next to your most recent Loan Estimate, work down Page 2 first, then run the five-page checklist below. If a lender-controlled fee moved when it should not have, you have language and a template to raise it today.";

export const HERO_NEXT_STEPS = [
  "Open your newest Loan Estimate and your Closing Disclosure side by side and compare the Page 2 fees line for line.",
  "Flag anything that grew in a zero-tolerance line or looks duplicated, then email your loan officer today — not the morning of closing.",
];

export const SECTIONS = [
  { id: "compare", title: "Compare your CD to your Loan Estimate" },
  { id: "page-two", title: "Page 2 fee audit and red flags" },
  { id: "checklist", title: "Page-by-page checklist (all 5 pages)" },
  { id: "escalate", title: "If something is wrong: how to escalate" },
  { id: "definitions", title: "Fee definitions, decoded" },
  { id: "faq", title: "Common questions" },
];

export interface ToleranceTier {
  id: string;
  tier: string;
  rule: string;
  fees: string;
  action: string;
}

export const TOLERANCE_TIERS: ToleranceTier[] = [
  {
    id: "zero",
    tier: "Zero tolerance",
    rule: "These generally cannot increase from your Loan Estimate at all unless a valid changed circumstance was disclosed to you in a revised Loan Estimate.",
    fees:
      "Origination charges, points you already locked, the lender's underwriting and processing fees, fees for services you were not allowed to shop for where the lender chose the provider, and transfer taxes.",
    action:
      "Any increase without a documented changed circumstance is normally cured by the lender — commonly as a lender credit on Page 2 or 3. Ask for it in writing.",
  },
  {
    id: "ten",
    tier: "10% cumulative tolerance",
    rule: "This group is measured as a bucket, not line by line. The total of the group can typically rise up to 10% above the Loan Estimate total.",
    fees:
      "Recording fees, and third-party services you were allowed to shop for where you chose a provider from the lender's written list.",
    action:
      "Add the group up on both documents. One line can jump if another falls, but if the bucket total is more than 10% higher, ask the lender to explain or cure the excess.",
  },
  {
    id: "unlimited",
    tier: "No tolerance limit",
    rule: "These legitimately move because they are market-driven, date-driven, or your own choice — they are not lender pricing.",
    fees:
      "Prepaid interest (per-diem, tied to your closing date), homeowners insurance premiums, property taxes and escrow deposits, and third-party services you shopped for outside the lender's provider list.",
    action:
      "Changes here are usually normal. Still confirm the math: check the number of per-diem days, the insurance premium against your policy, and the escrow months against Page 2's escrow table.",
  },
];

export interface RedFlag {
  id: string;
  section: string;
  label: string;
  charge: string;
  why: string;
}

export const PAGE_TWO_RED_FLAGS: RedFlag[] = [
  {
    id: "origination-stack",
    section: "Section A",
    label: "Origination charges",
    charge: "Origination fee plus a separate processing fee plus a separate underwriting fee",
    why:
      "Each of these can be legitimate, but stacked together they sometimes double-charge for one workflow. Ask what each one covers that the others do not, and confirm the total matches Section A on your Loan Estimate.",
  },
  {
    id: "points",
    section: "Section A",
    label: "Discount points",
    charge: "Points charged when you did not agree to buy the rate down",
    why:
      "Points appear at the top of Section A as a percentage and a dollar amount. If you locked a par rate, this line should be zero. If it is not, your rate lock and your fees disagree.",
  },
  {
    id: "admin",
    section: "Section A",
    label: "Vague administrative charges",
    charge: "\"Administration fee\", \"lender fee\", \"funding fee\" with no explanation",
    why:
      "Generic labels are the classic junk-fee shape. Ask for the specific service behind the label. These are lender-controlled and normally sit in zero tolerance.",
  },
  {
    id: "docprep",
    section: "Section B",
    label: "Document preparation",
    charge: "Document prep fee charged alongside an underwriting fee",
    why:
      "Preparing the loan documents is often already inside underwriting or origination. When both appear, ask which one is doing the work.",
  },
  {
    id: "courier",
    section: "Section B",
    label: "Courier, wire, and email fees",
    charge: "Courier fee, wire fee, and \"e-doc delivery\" fee on the same loan",
    why:
      "Small line items add up, and a delivery fee for documents sent electronically is worth questioning. Ask what was physically couriered.",
  },
  {
    id: "credit-report",
    section: "Section B",
    label: "Marked-up credit report fee",
    charge: "A credit report or verification fee that far exceeds the typical cost of the pull",
    why:
      "Third-party pass-through costs should look like pass-through costs. A number several times the market rate usually means a markup, and Section B is not a shoppable bucket.",
  },
  {
    id: "duplicate",
    section: "Sections A & B",
    label: "Duplicate services",
    charge: "The same service billed under two different names across A and B",
    why:
      "Read the two sections together, not separately. Appraisal review, tax service, and flood certification sometimes appear twice under slightly different wording.",
  },
];

export interface ChecklistPage {
  id: string;
  page: string;
  heading: string;
  items: string[];
}

export const PAGE_CHECKLIST: ChecklistPage[] = [
  {
    id: "page-1",
    page: "Page 1",
    heading: "Loan terms, projected payments, and costs at closing",
    items: [
      "Your legal name is spelled exactly as it appears on your ID, and the property address matches the contract line for line.",
      "The interest rate matches your signed rate lock, and \"Can this amount increase after closing?\" reads NO for the rate if you locked a fixed loan.",
      "Loan type is what you agreed to: fixed vs adjustable, and the loan term in years.",
      "Prepayment penalty and balloon payment both read NO unless you knowingly agreed to them.",
      "Estimated total monthly payment includes the escrow line you expected for taxes and insurance.",
      "Closing costs and Cash to Close on Page 1 match the detail on Pages 2 and 3.",
    ],
  },
  {
    id: "page-2",
    page: "Page 2",
    heading: "Closing cost details — the fee audit",
    items: [
      "Section A origination charges match your Loan Estimate, including points.",
      "Section B contains no duplicate or unexplained services (see the red flags above).",
      "Section C services you shopped for use the providers you actually chose.",
      "Section F prepaids: the number of per-diem interest days matches your closing date, and the insurance premium matches your policy.",
      "Section G escrow deposit shows the months you were quoted.",
      "Section H \"Other\" is real: owner's title insurance here should be optional and marked as such.",
      "Seller-paid and lender-paid columns are populated where credits were agreed.",
    ],
  },
  {
    id: "page-3",
    page: "Page 3",
    heading: "Calculating Cash to Close and the summaries",
    items: [
      "The Loan Estimate column vs Final column shows every change, and each \"YES\" has an explanation you understand.",
      "Your earnest money deposit is credited at the full amount you paid.",
      "Seller credits and lender credits appear at the negotiated amounts, not rounded down.",
      "Any deposit, adjustment, or payoff in the summaries matches your paperwork.",
      "The final Cash to Close is the number you will actually wire.",
    ],
  },
  {
    id: "page-4",
    page: "Page 4",
    heading: "Additional information about this loan",
    items: [
      "Assumption, demand feature, and negative amortization read as you expect.",
      "Late payment terms: confirm the grace period and the penalty amount.",
      "Escrow account section confirms whether an escrow account exists, the estimated annual amount, and what happens if it is waived.",
      "Partial payment policy is disclosed and acceptable to you.",
    ],
  },
  {
    id: "page-5",
    page: "Page 5",
    heading: "Loan calculations, disclosures, and contacts",
    items: [
      "Total of payments, finance charge, amount financed, APR, and total interest percentage are present and consistent with your loan terms.",
      "The APR is close to your note rate; a wide gap usually points back to fees on Page 2.",
      "Contact information for the lender, mortgage broker, real estate brokers, and settlement agent is complete, with license numbers.",
      "You know exactly who to email tonight if a number looks wrong.",
    ],
  },
];

export interface FeeDefinition {
  id: string;
  term: string;
  definition: string;
}

export const FEE_DEFINITIONS: FeeDefinition[] = [
  {
    id: "origination",
    term: "Origination charge",
    definition:
      "What the lender charges to make the loan. It sits in Section A, is lender-controlled, and generally falls under zero tolerance.",
  },
  {
    id: "points",
    term: "Discount points",
    definition:
      "An optional up-front payment to lower your interest rate. One point is 1% of the loan amount. If you did not buy down the rate, this should be zero.",
  },
  {
    id: "underwriting",
    term: "Underwriting fee",
    definition:
      "Charged for evaluating and approving your file. Reasonable on its own — question it when processing, document prep, and administration fees also appear.",
  },
  {
    id: "prepaid-interest",
    term: "Prepaid (per-diem) interest",
    definition:
      "Interest from your closing date to the end of that month. It changes whenever your closing date moves and is not a junk fee.",
  },
  {
    id: "escrow-deposit",
    term: "Escrow deposit",
    definition:
      "Money collected up front so the servicer can pay your taxes and insurance when due. Driven by your tax and premium amounts, not by lender pricing.",
  },
  {
    id: "title-lender-vs-owner",
    term: "Lender's vs owner's title insurance",
    definition:
      "The lender's policy protects the lender and is usually required. The owner's policy protects you and is typically optional — it should be labeled optional on Page 2.",
  },
  {
    id: "recording",
    term: "Recording fee",
    definition:
      "Paid to the county to record the deed and mortgage. It sits in the 10% cumulative tolerance bucket.",
  },
  {
    id: "lender-credit",
    term: "Lender credit",
    definition:
      "Money the lender applies against your costs. It is also the usual mechanism for curing a tolerance violation, so it is what you ask for when a zero-tolerance fee increased.",
  },
];

export const ESCALATION_STEPS = [
  {
    id: "cite",
    title: "Cite the exact location",
    body:
      "Write \"Page 2, Section B, line: Document Preparation Fee, $X\" rather than \"one of the fees looks high.\" Specific citations get specific answers.",
  },
  {
    id: "compare",
    title: "Attach the comparison",
    body:
      "Quote the Loan Estimate amount next to the Closing Disclosure amount and note which tolerance bucket the line belongs to.",
  },
  {
    id: "email",
    title: "Email the loan officer and copy your agent",
    body:
      "Email creates a timestamped record before closing. Copy your real estate agent — agents escalate fee disputes routinely and often know the lender's operations contact.",
  },
  {
    id: "credit",
    title: "Ask for a lender credit where justified",
    body:
      "If a zero-tolerance fee rose with no disclosed changed circumstance, ask specifically for a lender credit on the revised Closing Disclosure rather than a verbal promise to \"look into it.\"",
  },
  {
    id: "timing",
    title: "Raise it now, not at the table",
    body:
      "Certain changes — a higher APR beyond tolerance, a switch in loan product, or the addition of a prepayment penalty — restart the three-business-day review period. Most fee corrections do not. Either way, earlier is cheaper.",
  },
];

export const ESCALATION_EMAIL_TEMPLATE = `Subject: Closing Disclosure question — fee discrepancy before signing

Hi [Loan Officer],

Thanks for sending the Closing Disclosure for [property address], closing [date]. I compared it against my most recent Loan Estimate dated [LE date] and have a few questions before I sign:

1. Page 2, Section [A/B], "[fee name]" — Loan Estimate: $[LE amount]. Closing Disclosure: $[CD amount]. My understanding is this line falls under [zero tolerance / the 10% cumulative bucket]. Can you point me to the changed circumstance and the revised Loan Estimate that disclosed it?

2. Page 2, Section [A/B], "[fee name]" — this appears to overlap with "[other fee name]". Can you confirm what each covers?

If any of these increased without a disclosed changed circumstance, please issue a lender credit on a revised Closing Disclosure and send it over.

I want to keep our closing date, so an updated CD today would be great.

Thanks,
[Your name]
[Phone]`;

export interface Faq {
  id: string;
  q: string;
  a: string;
}

export const FAQS: Faq[] = [
  {
    id: "junk-fees",
    q: "What counts as a junk fee on a Closing Disclosure?",
    a: "There is no official category called a junk fee. In practice it means a lender-controlled charge that is vague, duplicated, or marked up well beyond the underlying cost — an unexplained administration fee, a document prep fee sitting next to an underwriting fee, or a credit report fee several times the market rate. Prepaid interest, taxes, and insurance are not junk fees even when they rise, because they are market and date driven rather than lender pricing.",
  },
  {
    id: "differs-from-le",
    q: "Why is my Closing Disclosure different from my Loan Estimate?",
    a: "Some differences are expected. Prepaid interest changes with your closing date, insurance and taxes come in at actual amounts, and shopped services reflect the provider you picked. What should not change quietly are zero-tolerance items like origination charges and locked points. Compare the Page 3 Loan Estimate vs Final columns first, then ask about every increase you cannot explain.",
  },
  {
    id: "three-day",
    q: "What restarts the three-business-day review period?",
    a: "The waiting period generally restarts for three specific changes: the APR becomes inaccurate beyond tolerance, a prepayment penalty is added, or the basic loan product changes, such as fixed to adjustable. Most fee corrections and small adjustments produce a revised Closing Disclosure without a new three-day wait. Ask your lender to confirm how any correction affects your date.",
  },
  {
    id: "when-contact",
    q: "When should I contact my lender about a problem?",
    a: "The same day you receive the Closing Disclosure. You have at least three business days by design, and the fixes that require a revised CD take time to produce. Raising a fee question the morning of closing is when buyers feel forced to sign anyway.",
  },
  {
    id: "refuse-to-sign",
    q: "Can I refuse to close if a fee is wrong?",
    a: "You are not obligated to sign a document you believe is inaccurate, but delaying a closing can carry real consequences under your purchase contract, including rate lock expiration and per-diem costs. That is exactly why the review happens during the three-day window instead of at the table. Talk to your real estate agent and, for contract questions, a real estate attorney.",
  },
  {
    id: "who-helps",
    q: "Who can help me review this for free?",
    a: "A HUD-approved housing counselor will walk through the document with you at no cost, and your real estate agent has seen hundreds of these. Neither replaces legal advice, but both catch common problems quickly.",
  },
];

export const COMPLIANCE_NOTE =
  "Tolerance categories and the three-business-day rule come from federal mortgage disclosure requirements, but how they apply depends on your loan type, your lender, and whether a changed circumstance was properly disclosed. Fee names vary between lenders and settlement agents. Nothing here determines whether a specific charge on your document is improper.";

export const DISCLAIMER =
  "Educational information only, not legal, tax, or lending advice. Home Approach is not a lender, a settlement agent, or a law firm. Confirm every figure with your lender and settlement agent, and consult a real estate attorney about contract or legal questions.";

export const CLOSING_DISCLOSURE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
